import { useFormik } from "formik";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Card, { CardBody } from "../../Components/base/Card";
import Button from "../../Components/base/forms/Button";
import FormGroup from "../../Components/base/forms/FormGroup";
import Input from "../../Components/base/forms/Input";
import SearchableSelect from "../../Components/base/forms/SearchableSelect";
import Textarea from "../../Components/base/forms/Textarea";
import OffCanvasDialog from "../../Components/base/OffCanvas";
import {
  getFYEData,
  storeFinancialYearEndDetails,
  syncAuditorName,
} from "../../Services/FinancialYearEndServices";
import { getUsersList } from "../../Services/UsersServices";
import { dateFormat, isBefore } from "../../Utils/Helpers";
import CommonRadioGroup from "../../Components/base/forms/CommonRadioGroup";

const EditFinancialYearEnd = ({
  isOpen,
  setIsOpen,
  setIsOpenCompleteModal,
  itemData = {},
  fyInternalReviewStatus
}) => {
  const dispatch = useDispatch();
  const { usersData } = useSelector((state) => state.users);
  const hasItemData = Object.keys(itemData).length > 0;
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (usersData?.length == 0) dispatch(getUsersList());
  }, [dispatch]);

  const usersList = usersData.map((user) => ({
    value: user.id,
    text: user.first_name + " " + user.last_name,
  }));

  const statusOptions = fyInternalReviewStatus?.map((status) => ({
    value: status.id,
    text: status.status_name,
  }));

  const formatDate = (date) =>
    date ? new Date(date).toISOString().split("T")[0] : "";

  const formik = useFormik({
    enableReinitialize: true, // ✅ Allow reinitialization when initialValues change
    initialValues: {
      FinYearEnd: dateFormat(itemData?.financial_year_end) || "",
      insuranceDueDate: dateFormat(itemData?.InsurancePolicy?.due_date) || "",
      auditor_name: itemData?.auditor_name || "",
      fy_account_completion_date:
        hasItemData && itemData?.completed_date
          ? formatDate(itemData?.completed_date)
          : "",
      fy_internal_review_status:
        hasItemData && itemData?.internal_review_status
          ? itemData?.internal_review_status
          : fyInternalReviewStatus[0]?.id,
      sent_auditor_date:
        hasItemData && itemData?.sent_auditor_date
          ? formatDate(itemData?.sent_auditor_date)
          : "",
      internal_review_by:
        hasItemData && itemData?.internal_review_by
          ? itemData?.internal_review_by
          : null,
      audit_required:
        hasItemData && itemData?.audit_required
          ? itemData?.audit_required
          : itemData?.auditor_name
            ? 1
            : 0,
      audit_report_received_date:
        hasItemData && itemData?.audit_report_received_date
          ? formatDate(itemData?.audit_report_received_date)
          : "",
      notes: hasItemData && itemData?.notes ? itemData?.notes : "",
      accounting_method: itemData?.accounting_method || "",
      account_manager: itemData?.account_manager_live || "",
    },
    validate: (values) => {
      const errors = {};
      return errors;
    },
    validateOnChange: true,
    onSubmit: (values, { resetForm }) => {
      handleSubmit(values);
      setIsOpen(false);
      resetForm();
    },
  });

  // Centralize the status calculation into one deterministic function instead of setting status inside every handler.
  const toKey = (name) => {
    return name
      ?.toUpperCase()
      .replace(/–/g, "-")
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
  };
  const createStatusMap = (statusList) => {
    return statusList.reduce((acc, curr) => {
      acc[toKey(curr.status_name)] = curr.id;
      return acc;
    }, {});
  };

  const resolveStatusCode = (values) => {
    const {
      internal_review_by,
      fy_account_completion_date,
      audit_required,
      sent_auditor_date,
      audit_report_received_date,
    } = values;

    const hasActionBy = !!internal_review_by;
    const hasAccountsDate = !!fy_account_completion_date;
    const hasSentDate = !!sent_auditor_date;
    const hasReportDate = !!audit_report_received_date;
    const isAuditRequired = Number(audit_required) === 1;

    if (
      hasActionBy &&
      hasAccountsDate &&
      isAuditRequired &&
      hasSentDate &&
      hasReportDate
    ) {
      return "COMPLETED_AUDITED";
    }

    if (hasActionBy && hasAccountsDate && isAuditRequired && hasSentDate) {
      return "WITH_AUDITOR";
    }

    if (hasActionBy && hasAccountsDate && isAuditRequired) {
      return "READY_FOR_AUDIT";
    }

    if (hasActionBy && hasAccountsDate && !isAuditRequired) {
      return "COMPLETED_NOT_AUDITED";
    }

    if (hasActionBy && !hasAccountsDate) {
      return "ASSIGNED";
    }

    return "PENDING_ACTION";
  };

  useEffect(() => {
    if (!fyInternalReviewStatus?.length) return;

    const statusMap = createStatusMap(fyInternalReviewStatus);
    const statusCode = resolveStatusCode(formik.values);
    const statusId = statusMap[statusCode];

    if (statusId && statusId !== formik.values.fy_internal_review_status) {
      formik.setFieldValue("fy_internal_review_status", statusId);
    }
  }, [
    formik.values.internal_review_by,
    formik.values.fy_account_completion_date,
    formik.values.audit_required,
    formik.values.sent_auditor_date,
    formik.values.audit_report_received_date,
    fyInternalReviewStatus,
  ]);

  /** Start Handlers Methods */
  const handleSubmit = async (values) => {
    try {
      const requestBody = {
        ...values,
        building_id: itemData?.building_id,
        audit_required: parseInt(values.audit_required),
        auditor_name: values.auditor_name,
        financial_year_end: itemData?.financial_year_end,
        fy_account_completion_date: values.fy_account_completion_date
          ? dateFormat(values.fy_account_completion_date, "YYYY-MM-DD")
          : null,
        sent_auditor_date: values.sent_auditor_date
          ? dateFormat(values.sent_auditor_date, "YYYY-MM-DD")
          : null,
        audit_report_received_date: values.audit_report_received_date
          ? dateFormat(values.audit_report_received_date, "YYYY-MM-DD")
          : null,
      };
      const response = await dispatch(
        storeFinancialYearEndDetails(requestBody),
      );
      if (response.status_code === 200) {
        toast.success("Building Details updated successfully.");
        dispatch(getFYEData());
      } else {
        toast.error(
          response.message ||
          "Building Details update failed. Please try again.",
        );
        console.error("Error building details update", response);
      }
    } catch (error) {
      console.error("API call error", error);
    }
  };

  const handleClose = () => {
    formik.resetForm();
    setIsOpen(false);
  };

  // Action By
  const handleActionByChange = (e) => {
    const selectedValues = e.target.value;
    formik.setFieldValue("internal_review_by", selectedValues);
  };

  const handleAccountsCompletionDateChange = (e) => {
    const accountsDate = e.target.value;
    const auditRequired = formik.values.audit_required;

    formik.setFieldValue("fy_account_completion_date", accountsDate);

    // TODO: CLEAR BTN , If cleared, reset dependent field
    if (!accountsDate) {
      formik.setFieldValue("sent_auditor_date", "");
      formik.setFieldTouched("sent_auditor_date", false);
    }
    // ---- END CLEAR BTN

    let status = fyInternalReviewStatus[2]?.id; // default

    if (accountsDate && auditRequired == 0) {
      status = fyInternalReviewStatus[3]?.id;
    }
    // Audit Required = YES → Awaiting BCM review logic
    else if (accountsDate && auditRequired == 1) {
      status = fyInternalReviewStatus[4]?.id; // Awaiting BCM review
    } else {
      status = fyInternalReviewStatus[1]?.id; // Assign
    }
  };

  const handleAuditRequiredChange = (e) => {
    const selectedValues = e.target.value;
    const prevStatus = formik.values.fy_internal_review_status;

    formik.setFieldValue("audit_required", selectedValues);

    let status;
    if (selectedValues == 1) {
      status = fyInternalReviewStatus[4]?.id;
    } else if (selectedValues == 0) {
      status = fyInternalReviewStatus[3]?.id;
    } else {
      status = prevStatus;
    }
  };

  const handleSentAuditorDateChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue("sent_auditor_date", value);

    // TODO: CLEAR BTN , If cleared, reset dependent field
    if (!value) {
      formik.setFieldValue("audit_report_received_date", "");
      formik.setFieldTouched("audit_report_received_date", false);
    }
    // ---- END CLEAR BTN

    const status =
      value === ""
        ? fyInternalReviewStatus[4]?.id
        : fyInternalReviewStatus[5]?.id;
  };

  const handleAuditReportReceivedDateChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue("audit_report_received_date", value);

    // Apply status only if a date is selected
    const status =
      value !== ""
        ? fyInternalReviewStatus[6]?.id
        : fyInternalReviewStatus[5]?.id;
  };

  const handleRefreshAuditorName = async (e) => {
    e.preventDefault();
    try {
      formik.setFieldValue("auditor_name", "");
    } catch (error) {
      console.error("API call error", error);
    }
  };

  //Refresh Auditor Name
  /** 
  const handleRefreshAuditorName = async (e) => {
    e.preventDefault();
    try {
      const requestBody = {
        building_id: itemData?.building_id,
      };
      const response = await dispatch(syncAuditorName(requestBody));
      if (response.status_code === 200) {
        toast.success("Auditor Name synced successfully.");
        formik.setFieldValue(
          "auditor_name",
          response?.data?.auditor_name || "",
        );
        formik.setFieldValue(
          "audit_required",
          response?.data?.audit_required || 0,
        );
        dispatch(getFYEData());
      } else {
        toast.error(
          response.message || "Auditor Name sync failed. Please try again.",
        );
        console.error("Error Auditor Name sync", response);
      }
    } catch (error) {
      console.error("API call error", error);
    }
  };
  */
  /** Handle Methods END */

  return (
    <OffCanvasDialog
      isOpen={isOpen}
      setOpen={handleClose}
      title="Edit Financial Year End"
      subTitle={`${itemData?.building_name} - ${itemData?.building_number}`}
      placement="end"
    >
      <div className="row g-4">
        <div className="col-12">
          Financial Year End: {formik.values.FinYearEnd || "-"}
        </div>
        <div className="col-12">
          Insurance Due Date: {formik.values.insuranceDueDate || "-"}
        </div>
        <div className="col-12">
          <FormGroup id="internal_review_by" label="Action By">
            <div className="position-relative">
              <SearchableSelect
                name="internal_review_by"
                list={usersList}
                placeholder="Select Status"
                onChange={(e) => handleActionByChange(e)}
                onBlur={formik.handleBlur}
                value={formik.values.internal_review_by}
                search={true}
                isValid={formik.isValid}
                error={formik.errors.internal_review_by}
                touched={formik.touched.internal_review_by}
              />
              {formik.values.internal_review_by && (
                <Button
                  icon="CircleX"
                  isLight
                  className="btn-only-icon position-absolute top-50 end-0 translate-middle-y me-5"
                  onClick={() => {
                    formik.setFieldValue("internal_review_by", null);
                    formik.setFieldValue(
                      "fy_internal_review_status",
                      fyInternalReviewStatus[0]?.id,
                    );
                    // formik.setFieldValue("fy_account_completion_date", null);
                  }}
                />
              )}
            </div>
          </FormGroup>
        </div>
        <div className="col-12">
          <FormGroup
            id="fy_account_completion_date"
            label="Accounts completion date"
          >
            <div className="position-relative">
              <Input
                type="date"
                onChange={handleAccountsCompletionDateChange}
                onBlur={formik.handleBlur}
                value={formik.values.fy_account_completion_date}
                isValid={formik.isValid}
                isTouched={formik.touched.fy_account_completion_date}
                invalidFeedback={formik.errors.fy_account_completion_date}
                className="pr-5"
                disabled={!formik.values.internal_review_by}
              />
            </div>
          </FormGroup>
        </div>

        <div className="col-12" style={{ paddingRight: 0 }}>
          <CommonRadioGroup
            name="audit_required"
            label="Audit Required"
            onChange={(e) => handleAuditRequiredChange(e)}
            value={formik.values.audit_required}
            error={formik.errors.audit_required}
            touched={formik.touched.audit_required}
            options={[
              { label: "Yes", value: 1 },
              { label: "No", value: 0 },
            ]}
          />
        </div>
        {formik.values.audit_required == 1 ? (
          <Card isCompact className="shadow-none border border-2 mb-0 mx-1 p-3">
            <CardBody className="row g-4 p-0">
              <div className="col-12">
                <div className="d-flex align-items-end">
                  <FormGroup
                    id="auditor_name"
                    label="Auditor Name"
                    className="col-10"
                  >
                    <Input
                      onChange={formik.handleChange}
                      name="auditor_name"
                      value={formik.values.auditor_name ?? ""}
                    />
                  </FormGroup>
                  <Button
                    icon="CircleX"
                    color="dark"
                    isLight
                    className="btn-only-icon position-relative ms-3"
                    aria-label="Remove Auditor Name"
                    onClick={(e) => handleRefreshAuditorName(e)}
                  />
                  {/* <Button
                    icon="RefreshCcw"
                    color="dark"
                    isLight
                    className="btn-only-icon position-relative ms-3"
                    aria-label="Refresh Auditor Name"
                    onClick={(e) => handleRefreshAuditorName(e)}
                  /> */}
                </div>
              </div>
              <div className="col-12">
                <FormGroup id="sent_auditor_date" label="Sent To Auditor Date">
                  <Input
                    onChange={(e) => handleSentAuditorDateChange(e)}
                    onBlur={formik.handleBlur}
                    type="date"
                    value={formik.values.sent_auditor_date}
                    isValid={formik.isValid}
                    isTouched={formik.touched.sent_auditor_date}
                    invalidFeedback={formik.errors.sent_auditor_date}
                    className="pr-5"
                    disabled={!formik.values.fy_account_completion_date}
                  />
                </FormGroup>
              </div>
              <div className="col-12">
                <FormGroup
                  id="audit_report_received_date"
                  label="Audit report received date"
                >
                  <Input
                    onChange={(e) => handleAuditReportReceivedDateChange(e)}
                    onBlur={formik.handleBlur}
                    type="date"
                    value={formik.values.audit_report_received_date}
                    isValid={formik.isValid}
                    className="pr-5"
                    disabled={!formik.values.sent_auditor_date}
                  />
                </FormGroup>
              </div>
            </CardBody>
          </Card>
        ) : (
          ""
        )}

        <div className="col-12">
          <FormGroup label="Status">
            <SearchableSelect
              name="fy_internal_review_status"
              list={statusOptions}
              placeholder="Select Status"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.fy_internal_review_status}
              search={true}
              isValid={formik.isValid}
              error={formik.errors.fy_internal_review_status}
              touched={formik.touched.fy_internal_review_status}
            // Not editable when user logged in
            // disabled={
            //   user?.role.name !== "Admin" && user?.role.name !== "Super Admin"
            // }
            />
          </FormGroup>
        </div>

        <div className="col-12">
          <FormGroup id="notes" label="Notes">
            <Textarea
              onChange={formik.handleChange}
              value={formik.values.notes}
            />
          </FormGroup>
        </div>

        <div className="col-12 mt-5">
          <Button
            color="primary"
            className="w-100"
            style={{ padding: "0.65rem", fontSize: "1.15rem" }}
            onClick={formik.handleSubmit}
          >
            Save
          </Button>
        </div>
      </div>
    </OffCanvasDialog>
  );
};

export default EditFinancialYearEnd;
